import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { DataService } from './../../../core/service/base/data.service';

import { APP_CONFIG, IAppConfig } from './../../../app.config';
import { Observable, of } from 'rxjs';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { MessageService } from '../../../core/service/message.service';
import { Entrance, EntrancePostTarget } from '../../model/entrance';
import { AutoCompleteSelectionItem, IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { AutoCompleteSelectionState } from '../../../core/component/autocomplete-remote/autocomplete-service.interface';
import { catchError, finalize, map } from 'rxjs/operators';
import { IPostTargetLoader } from '../base/post-target-loader.interface';

type EntranceSelectionOptions = {
  companyIds?: string[];
  propertyIds?: string[];
  buildingIds?: string[];
};

@Injectable()
export class EntranceService extends DataService<Entrance> implements IAutoCompleteService, IPostTargetLoader<EntrancePostTarget> {
  constructor(
    client: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    private _messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(client, config, _messageService, Entrance, progressBar, 'entrance');
  }

  protected createModel(modelData: any): Entrance {
    try {
      return new Entrance(modelData);
    } catch (e) {
      console.log(e);
      throw new Error('Invalid data format.');
    }
  }

  getSelectionItem(id: string, state?: AutoCompleteSelectionState<any, EntranceSelectionOptions | string | string[]>): Observable<AutoCompleteSelectionItem | null> {
    let params = new HttpParams();
    const options = state?.options;
    if (this.isLegacyOptions(options)) {
      for (const buildingId of this.toIds(options)) {
        params = params.append('options', buildingId);
      }
    } else {
      for (const companyId of this.toIds(options?.companyIds)) {
        params = params.append('companyIds', companyId);
      }
      for (const propertyId of this.toIds(options?.propertyIds)) {
        params = params.append('propertyIds', propertyId);
      }
      for (const buildingId of this.toIds(options?.buildingIds)) {
        params = params.append('buildingIds', buildingId);
      }
    }

    return this.client.get<any>(`${this.endpoint}/selectionItem/${id}`, { params }).pipe(
      map(item => new AutoCompleteSelectionItem(item.name, item.id, item, item.shortName)),
      catchError((error: HttpErrorResponse) => {
        if (params.keys().length > 0 && error?.status === 404) {
          return of(null);
        }
        this._messageService.showError(error);
        return [];
      })
    );
  }

  getSelection(filter: string, options?: EntranceSelectionOptions | string | string[], page?: number): Observable<AutoCompleteSelectionItem[]> {
    const queryArgs: { filter: string; maxItems: number; options?: string[]; companyIds?: string[]; propertyIds?: string[]; buildingIds?: string[]; page?: number } = {
      filter,
      maxItems: 20,
    };
    if (this.isLegacyOptions(options)) {
      const buildingIds = this.toIds(options);
      if (buildingIds.length) queryArgs.options = buildingIds;
    } else {
      const companyIds = this.toIds(options?.companyIds);
      const propertyIds = this.toIds(options?.propertyIds);
      const buildingIds = this.toIds(options?.buildingIds);
      if (companyIds.length) queryArgs.companyIds = companyIds;
      if (propertyIds.length) queryArgs.propertyIds = propertyIds;
      if (buildingIds.length) queryArgs.buildingIds = buildingIds;
    }
    if (page > 0) queryArgs.page = page;

    return this.client.post<any[]>(`${this.endpoint}/selection`, queryArgs).pipe(
      map(data => data.map(item => new AutoCompleteSelectionItem(item.name, item.id, item, item.shortName))),
      catchError(e => {
        this._messageService.showError(e);
        return of([]);
      })
    );
  }

  private isLegacyOptions(options: EntranceSelectionOptions | string | string[] | undefined): options is string | string[] {
    return typeof options === 'string' || Array.isArray(options);
  }

  private toIds(value?: string | string[]): string[] {
    return (Array.isArray(value) ? value : value ? [value] : []).filter(Boolean);
  }

  getPostTargetModels(buildingId: string): Observable<EntrancePostTarget[]> {
    this.progressBar.start();
    return this.client.get<EntrancePostTarget[]>(`${this.endpoint}/post-target/${buildingId}`).pipe(
      map(response => response.map(x => new EntrancePostTarget(x))),
      finalize(() => this.progressBar.stop())
    );
  }
}
